import { Observable } from "rxjs";
import NetworkService from "../network/networkService";
import { AgTechQueryParam, OdQueryParam } from "../enums";
import { helperFunctions } from "../helper";
import { IRegionAllResponse, IRegion, ICreateRegionRequest, IPatchOperation } from "../models/IRegion.model";
import { URLConfig } from "../network/urlStore";

export const getRegions = (
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<IRegionAllResponse> => {
  let url = URLConfig.getRegionsURL();
  for (const param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IRegionAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching Regions data", error);
      }
    );
  });
};

export const getRegionById = (regionId: number): Observable<IRegion> => {
  const url = URLConfig.getRegionByIdURL(regionId);
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IRegion;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching Region by ID", error);
      }
    );
  });
};

export const getRegionsNextPage = (
  nextPageURI: string
): Observable<IRegionAllResponse> => {
  const url = URLConfig.getRegionsNextPageURL(nextPageURI);
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res) => {
        if (res) {
          const response = res as unknown as IRegionAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log("Error while fetching Regions next page ", error);
      }
    );
  });
};

export const getContentLengthForRegions = (
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<number> => {
  let url = URLConfig.getContentLengthForRegionsURL();
  for (const param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }

  return new Observable((subscriber) => {
    NetworkService.head(url).then(
      (response: unknown) => {
        if (response) {
          const { contentLength } = response as { contentLength: number };
          subscriber.next(contentLength);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching count for Regions", error);
      }
    );
  });
};

export const createRegion = (regionData: ICreateRegionRequest): Observable<IRegion> => {
  const url = URLConfig.createRegionURL();
  return new Observable((subscriber) => {
    NetworkService.post(url, regionData).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IRegion;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while creating Region", error);
      }
    );
  });
};

export const updateRegion = (
  regionId: string,
  patchOperations: IPatchOperation[]
): Observable<IRegion> => {
  const url = URLConfig.updateRegionURL(regionId);
  return new Observable((subscriber) => {
    NetworkService.patch(url, patchOperations).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IRegion;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while updating Region", error);
      }
    );
  });
};

export const deleteRegion = (regionId: string): Observable<void> => {
  const url = URLConfig.deleteRegionURL(regionId);
  return new Observable((subscriber) => {
    NetworkService.delete(url).then(
      () => {
        subscriber.next();
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while deleting Region", error);
      }
    );
  });
};

// Helper function to build patch operations for region updates
export const buildRegionPatchOperations = (updates: {
  name?: string;
  countryCode?: string;
  boundary?: any;
}): IPatchOperation[] => {
  const operations: IPatchOperation[] = [];

  if (updates.name !== undefined) {
    operations.push({
      op: "replace",
      path: "/name",
      value: updates.name
    });
  }

  if (updates.countryCode !== undefined) {
    operations.push({
      op: "replace",
      path: "/countryCode",
      value: updates.countryCode
    });
  }

  if (updates.boundary !== undefined) {
    operations.push({
      op: "replace",
      path: "/Boundary",
      value: updates.boundary
    });
  }

  return operations;
};

const RegionsService = {
  getRegions,
  getRegionById,
  getRegionsNextPage,
  getContentLengthForRegions,
  createRegion,
  updateRegion,
  buildRegionPatchOperations,
  deleteRegion,
};

export default RegionsService;
